<script setup>
import {ref, watch} from 'vue';
import FormSelect from "@/components/ui/form/FormSelect.vue";
import {useI18n} from "vue-i18n";

const {t} = useI18n();

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['submit', 'cancel']);

const form = ref({
  name: '',
  type: 'primary',
  translations: {uk: '', en: '', pl: '', cz: ''},
  ...props.initialData
});

const errors = ref({});
const isSubmitting = ref(false);

const languages = [
  {code: 'uk', label: 'Українська'},
  {code: 'en', label: 'English'},
  {code: 'pl', label: 'Polski'},
  {code: 'cz', label: 'Čeština'}
];

const typeOptions = [
  {value: 'primary', label: 'Primary'},
  {value: 'secondary', label: 'Secondary'},
  {value: 'success', label: 'Success'},
  {value: 'danger', label: 'Danger'},
  {value: 'warning', label: 'Warning'},
  {value: 'info', label: 'Info'},
  {value: 'light', label: 'Light'},
  {value: 'dark', label: 'Dark'}
];

watch(() => props.initialData, (newData) => {
  if (newData) {
    form.value = {
      name: newData.name || '',
      type: newData.type || 'primary',
      translations: {
        uk: newData.translations?.uk || '',
        en: newData.translations?.en || '',
        pl: newData.translations?.pl || '',
        cz: newData.translations?.cz || ''
      }
    };
  }
}, {deep: true, immediate: true});

const validate = () => {
  errors.value = {};

  if (!form.value.name || form.value.name.trim() === '') {
    errors.value.name = t('validation.required', {field: t('tableheadings.type')});
  }

  if (!form.value.type) {
    errors.value.type = t('validation.required', {field: t('tableheadings.type')});
  }

  return Object.keys(errors.value).length === 0;
};

const handleSubmit = () => {
  if (!validate()) {
    return;
  }

  isSubmitting.value = true;
  emit('submit', {...form.value});

  setTimeout(() => {
    isSubmitting.value = false;
  }, 500);
};
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <div class="mb-3">
      <label for="name" class="form-label">{{ t('tableheadings.title') }}</label>
      <input
          id="name"
          v-model="form.name"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.name }"
      />
      <div v-if="errors.name" class="invalid-feedback">
        {{ errors.name }}
      </div>
    </div>

    <div class="mb-3">
      <label for="type" class="form-label">{{ t('tableheadings.type') }}</label>
      <FormSelect
          id="type"
          v-model="form.type"
          :options="typeOptions"
          :class="{ 'is-invalid': errors.type }"
      />
      <div v-if="errors.type" class="invalid-feedback">
        {{ errors.type }}
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label">{{ t('labels.translations') }}</label>
      <div v-for="lang in languages" :key="lang.code" class="input-group mb-2">
        <span class="input-group-text" style="min-width: 120px;">{{ lang.label }}</span>
        <input
            v-model="form.translations[lang.code]"
            type="text"
            class="form-control"
            :placeholder="form.name"
        />
      </div>
    </div>

    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-danger" @click="$emit('cancel')">
        {{ t('buttons.cancel') }}
      </button>
      <button type="submit" class="btn btn-success" :disabled="isSubmitting">
        <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </span>
        {{ t('buttons.save') }}
      </button>
    </div>
  </form>
</template>